import { z } from 'zod'
import {
  ResolvedOperand,
  maybeString,
  maybeStringArray,
} from '@omnea-app/packages/universal/conditionality/evaluation/evaluation'
import { AttachedFileSchema } from '@omnea-app/packages/universal/form/elements/attachedFile'
import {
  BaseFormAnswerSchema,
  BaseFormQuestionSchema,
} from '@omnea-app/packages/universal/formBuilder/BaseFormQuestionAndAnswerSchemas'
import { RequestFormCustomFieldPropertySchema } from '@omnea-app/packages/universal/formBuilder/customFieldTypes'
import { ExtractedAttributeSchema } from 'utils/documents/extracted-attribute'

export enum FileUploadType {
  ZIP = 'zip',
  IMAGE = 'image',
  /** @deprecated - we've removed the ability to add this option when configuring supported types
   * but we still  need to support it here for existing config.*/
  ALL_DOC_TYPES = 'document', // doc, docx, xls, xlsx, csv
  PDF = 'pdf',
  CSV = 'csv',
  WORD = 'word',
  EXCEL = 'excel',
}

export { FileUploadType as UploadableFileType }

export const ALL_FILE_UPLOAD_TYPES = [
  FileUploadType.ZIP,
  FileUploadType.IMAGE,
  FileUploadType.PDF,
  FileUploadType.CSV,
  FileUploadType.WORD,
  FileUploadType.EXCEL,
]

export const FilesFormQuestionSchema = BaseFormQuestionSchema.extend({
  type: z.literal('files'),
  supportedTypes: z.array(z.nativeEnum(FileUploadType)),
  multipleFiles: z.boolean(),
  storeFilesGlobally: z.boolean().optional(),
  allowAllDocumentTypes: z.boolean().optional(),
  documentExtractionEnabled: z.boolean().optional(),
  documentTypeId: z.string().optional(),
  customField: RequestFormCustomFieldPropertySchema.optional(),
  templateFiles: z.array(AttachedFileSchema).optional(),
})
export type FilesFormQuestion = z.infer<typeof FilesFormQuestionSchema>

export const REVIEW_OUTCOMES = ['high', 'medium', 'pass'] as const
export type ReviewOutcome = (typeof REVIEW_OUTCOMES)[number]

export const outcomeSchema = z.enum(REVIEW_OUTCOMES)

export const DocumentReviewResultSchema = z.object({
  documentReviewId: z.string().uuid(),
  outcome: outcomeSchema,
  status: z.enum(['success', 'partial']),
  playbookId: z.string().uuid(),
  reviewedAt: z.string().datetime({ offset: true }),
})
export type DocumentReviewResult = z.infer<typeof DocumentReviewResultSchema>

export const UploadedFileItemSchema = z.object({
  filename: z.string(),
  key: z.string(),
  extractedAttributes: z.array(ExtractedAttributeSchema).optional(),
  documentReviewResult: DocumentReviewResultSchema.optional(),
})

export type UploadedFileItem = z.infer<typeof UploadedFileItemSchema>

export const FilesFormAnswerSchema = BaseFormAnswerSchema.extend({
  type: z.literal('files'),
  value: z.array(UploadedFileItemSchema),
  storeFilesGlobally: z.boolean().optional(),
  customField: RequestFormCustomFieldPropertySchema.optional(),
})
export type FilesFormAnswer = z.infer<typeof FilesFormAnswerSchema>

export enum FileConditionVariants {
  DOCUMENT_REVIEW_OUTCOME = 'DOCUMENT_REVIEW_OUTCOME',
}

const outcomeSeverity = (outcome: string): number => {
  switch (outcome) {
    case 'pass':
      return 0
    case 'medium':
      return 1
    case 'high':
    default:
      // Unknown outcomes treated as high severity (conservative)
      return 2
  }
}

const HIGH_FALLBACK = { outcome: 'high', severity: outcomeSeverity('high') } as const

const resolveFileOutcome = (file: Pick<UploadedFileItem, 'documentReviewResult'>): { outcome: string; severity: number } => {
  // Missing documentReviewResult or outcome → treated as 'high' (conservative fallback)
  if (file.documentReviewResult?.outcome == null) return HIGH_FALLBACK
  const outcome = file.documentReviewResult.outcome
  return { outcome, severity: outcomeSeverity(outcome) }
}

export const getDocumentReviewOutcomeConditionValue = (
  files: Pick<UploadedFileItem, 'documentReviewResult'>[],
  variant: string | null
): ResolvedOperand<'string'> | null => {
  if (variant !== FileConditionVariants.DOCUMENT_REVIEW_OUTCOME) return null
  if (files.length === 0) return null

  // If no files have a review result, the question likely has no playbook configured.
  // Return null (not evaluable) rather than a false-positive 'high'.
  if (!files.some((f) => f.documentReviewResult != null)) return null

  const worst = files.map(resolveFileOutcome).reduce((a, b) => (b.severity > a.severity ? b : a))

  return maybeString(worst.outcome)
}

export const getFilesUploadedConditionValue = (files: Pick<UploadedFileItem, 'key'>[]): ResolvedOperand<'stringArray'> | null => {
  return maybeStringArray(files.map((f) => f.key))
}
